import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronDown, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fallback projects when backend is not available (same as backend seed data)
const FALLBACK_PROJECTS = [
  { id: 'fb-1', company: 'allstate', title: 'Underwriting Technology Roll-out Audit & Advisory', description: "Audited the launch of new insurance underwriting software to verify it was accurate, fair, and secure, ensuring a smooth go-live by catching issues before they could impact customers or the business.", context: "An Allstate subsidiary was upgrading the software agents use to generate insurance quotes and bind policies across multiple product lines. Internal Audit was brought in early to provide assurance before launch.", risk: "Early involvement by Internal Audit was critical. Automated systems, if not properly validated, can make incorrect decisions at scale, affecting customers and exposing the company to unintended risk.", action: "Reviewed the rules the software uses to make automatic decisions, and assessed system readiness across key areas, including situations requiring human review, new user setup, and access controls.", outcome: "Internal Audit identified and helped resolve critical control gaps before go-live, ensuring the software launched securely and within the company's approved risk guidelines.", tags: ['Underwriting', 'Technology', 'Risk Assessment'] },
  { id: 'fb-2', company: 'allstate', title: 'Claims Centre Implementation Audit & Advisory', description: "Provided audit oversight during a high-risk claims system transition, identifying a significant financial exposure and helping the business put the right safeguards in place before it became a real problem.", context: "An Allstate subsidiary was transitioning to a new claims processing system while keeping the old one running in parallel. Internal Audit was engaged to provide an independent check during this vulnerable period.", risk: "Parallel system environments are inherently high-risk. Without Independent Audit oversight, monitoring gaps could have led to duplicate claim payouts at significant cost to the business.", action: "Reviewed access controls, authorization limits, automation gaps, and data transfer accuracy between systems. Recommended stronger controls aligned with financial reporting standards (SOX).", outcome: "Internal Audit's early identification of a duplicate processing risk allowed the business to fix controls before go-live, preventing significant potential losses and strengthening compliance.", tags: ['Claims', 'SOX', 'Financial Impact'] },
  { id: 'fb-3', company: 'allstate', title: 'Regulatory Compliance Audit', description: "Conducted an independent review of a subsidiary's compliance program, giving leadership the assurance and roadmap needed to strengthen how the company manages its regulatory obligations.", context: "At the request of Allstate's Law & Regulation team, Internal Audit was engaged to provide an objective assessment of whether a subsidiary's compliance program was well-designed and functioning as intended, not just on paper.", risk: "Regulators expect more than a compliance program that exists on paper. Internal Audit plays a key role in independently verifying that programs are current, clearly owned, and visible to leadership before a regulator finds otherwise.", action: "Reviewed the compliance framework for completeness and currency. Assessed whether roles and responsibilities were clearly assigned. Verified the system for tracking regulatory changes. Reviewed the Chief Compliance Officer's (CCO) reporting structure to the board against regulatory expectations, and assessed leadership's visibility into ground-level compliance activity.", outcome: "Internal Audit surfaced actionable gaps in role clarity and leadership visibility, and delivered concrete recommendations to strengthen the CCO's reporting structure, helping the subsidiary get ahead of regulatory expectations rather than react to them.", tags: ['Regulatory Compliance', 'Governance', 'Controls'] },
  { id: 'fb-4', company: 'allstate', title: 'Anti-Money Laundering (AML) Audit', description: "Led the annual AML audit to independently confirm that Allstate's financial services arm had the right systems and processes in place to detect suspicious activity, providing regulators and leadership with confidence in the program.", context: "Allstate Financial Services is required by law to monitor transactions for signs of money laundering or dealings with sanctioned individuals or countries. Internal Audit provides annual independent validation of this program.", risk: "AML compliance isn't just a box to check. Regulators can impose significant penalties for gaps in monitoring. Independent audit validation is a critical line of defense in confirming the program works as required.", action: "Verified that transaction monitoring systems were functioning correctly, including alerts from the brokerage partner (NFS) and checks against government sanctions lists (OFAC). Also initiated automation efforts to make future audits faster and more efficient.", outcome: "Internal Audit confirmed full regulatory compliance and proactively identified opportunities to streamline the audit process through automation, adding lasting value beyond the audit itself.", tags: ['AML', 'FINRA', 'Compliance'] },
  { id: 'fb-5', company: 'allstate', title: 'Third-Party Risk Management (TPRM) Audit', description: "Assessed how well Allstate Canada's vendor oversight program stacked up against regulatory expectations, giving leadership a clear picture of where the program was strong and where it needed to improve.", context: "Canada's top financial regulator (OSFI) requires insurers to have robust programs for managing risks tied to third-party suppliers and technology partners. Internal Audit was engaged to provide an independent assessment.", risk: "Third-party relationships introduce risks that aren't always visible internally. Independent audit review is essential to surface vendor oversight gaps before they turn into regulatory findings or operational incidents.", action: "Worked directly with the Chief Risk Officer to benchmark the company's vendor management practices against OSFI's B-10 guidelines, covering outsourcing oversight, contract enforcement, and cybersecurity controls.", outcome: "Internal Audit identified specific gaps in vendor contract monitoring and flagged cybersecurity risks in third-party systems, giving leadership a prioritized roadmap to strengthen the program ahead of regulatory scrutiny.", tags: ['TPRM', 'OSFI', 'Cyber Risk'] },
  { id: 'fb-6', company: 'kpmg', title: 'Aerospace Manufacturing BI & P2P Optimization', description: "Helped a precision aerospace parts manufacturer unlock better decision-making by improving how they track purchasing, manage vendors, and oversee payments, turning data into a strategic advantage.", context: "The client lacked clear visibility into procurement performance and vendor behaviour. Bringing in an external lens helped surface patterns and risks that internal teams were too close to see.", risk: "Without independent analysis, inefficient vendor selection and weak payment oversight can quietly erode margins and create supply chain vulnerabilities that only become visible when it's too late.", action: "Built executive dashboards covering inventory, payables, and purchasing trends. Analysed vendor data to identify delivery performance patterns and flagged weaknesses in payment processing and invoice management.", outcome: "Delivered clearer, faster insights to leadership, improved vendor evaluation processes, and reduced the risk of payment and invoicing errors, giving the business a stronger foundation for procurement decisions.", tags: ['BI', 'P2P', 'Manufacturing'] },
  { id: 'fb-7', company: 'kpmg', title: 'Order-to-Cash Invoicing Optimization', description: "Helped a major Indian IT company get control of a high-error invoicing process, improving financial accuracy and reducing the operational burden that billing mistakes were creating across the business.", context: "The company's billing process had a high error rate that was affecting financial reporting and client relationships. An external review helped identify the structural root causes and a clear path to fix them.", risk: "Invoicing errors don't just affect revenue, they erode client trust and distort the financial data leadership relies on to make decisions. Catching and fixing these systematically is far more effective than correcting errors after the fact.", action: "Reorganized teams into focused, cross-functional groups (PODs) where each group owned the end-to-end invoicing process for specific clients. Built dashboards so managers could monitor accuracy and catch issues earlier.", outcome: "Achieved a 25% reduction in invoicing errors and improved the reliability of financial reports, giving leadership more accurate data for planning and forecasting.", tags: ['O2C', 'Automation', 'IT Services'] },
  { id: 'fb-8', company: 'kpmg', title: 'Express Logistics Operations Review', description: "Conducted a wide-ranging operational review for a logistics company, uncovering hidden financial exposures and process gaps, and helping the business recover funds it didn't know it was missing.", context: "The client needed an independent end-to-end assessment of their operations across delivery, finance, and customer support. An external review provided the objectivity needed to identify what internal teams had missed.", risk: "Operational blind spots in fast-moving logistics businesses can quietly compound. Without independent review, untracked funds and weak internal controls can go undetected for extended periods.", action: "Analyzed bank records and flagged unidentified incoming transfers. Identified weaknesses in employee advance payment controls. Benchmarked customer complaint resolution times against industry standards.", outcome: "Recovered ~INR 104 lakhs in previously untracked funds, put controls in place to protect against ~INR 20 lakhs in annual exposure, and identified process improvements with the potential to speed up customer response times by 40%.", tags: ['Logistics', 'Process Optimization', 'Revenue'] },
  { id: 'fb-9', company: 'kpmg', title: 'Multi-Specialty Hospitals Process Review', description: "Helped a hospital chain identify significant revenue that was quietly slipping through the cracks due to billing system errors, giving leadership the insight needed to fix the root causes and protect the bottom line.", context: "The hospital group needed an independent review of how revenue was being captured across billing, insurance, and operations. External analysis provided the depth and objectivity to surface issues that internal reporting had not flagged.", risk: "Billing errors in healthcare don't just affect revenue, they can also signal compliance risks with insurers. Independent review is often the only way to quantify losses that are spread across thousands of individual transactions.", action: "Used data analytics to trace billing errors through the SAP system, reviewed how insurance payments were being matched to invoices, and reconciled cash and bank records to identify discrepancies.", outcome: "Identified ~INR 10.91 lakhs in revenue leaking out every two weeks through billing errors, and ~INR 4.15 lakhs being recorded as income before it was earned (representing roughly 5% of total revenue), enabling the business to act on findings that would otherwise have continued undetected.", tags: ['Healthcare', 'Revenue Assurance', 'SAP'] },
  { id: 'fb-10', company: 'kpmg', title: 'SOX 404 Compliance Testing Lead', description: "Worked with a cross-border audit team through a rigorous financial controls review for a large European multinational, catching a material accounting error and building the client's internal capability to sustain compliance independently.", context: "The client, a publicly listed Dutch conglomerate, is required under US law (SOX Section 404) to annually verify that internal financial controls are well-designed and working. External audit support ensured the review was thorough and objective.", risk: "SOX compliance failures can have serious consequences for public companies, from investor mistrust to regulatory action. Independent testing is essential to catch issues that internal teams, who are close to the process, might overlook.", action: "Led end-to-end audit execution across Poland and Panama, covering process walkthrough and documentation, control design assessment, and operational testing. Managed and co-ordintaed with a team of four through structured knowledge-sharing sessions and process walkthroughs.", outcome: "Identified and escalated a recurring accounting error causing significant quarterly overstatements, enabling the client to correct it before it reached investors. Also built lasting internal capability by transferring audit knowledge across key finance functions, reducing reliance on external support in future cycles.", tags: ['SOX 404', 'Internal Controls', 'Multinational'] },
];

// Company data with logo representations
const companies = {
  allstate: {
    name: 'Allstate',
    logo: '/allstate-logo.png',
    color: 'vedic-gold',
    description: 'Leading insurance provider in North America'
  },
  kpmg: {
    name: 'KPMG',
    logo: '/kpmg-logo.png',
    color: 'deep-ochre',
    description: 'Global professional services network'
  }
};

function ProjectCard({ project, isExpanded, onToggle }) {
  const company = companies[project.company];
  
  const colorClasses = {
    'vedic-gold': {
      border: 'border-vedic-gold/30',
      text: 'text-vedic-gold',
      bg: 'bg-vedic-gold/10'
    },
    'deep-ochre': {
      border: 'border-deep-ochre/30',
      text: 'text-deep-ochre',
      bg: 'bg-deep-ochre/10'
    }
  };

  const colors = colorClasses[company?.color || 'vedic-gold'];

  return (
    <motion.div
      layout
      data-testid={`project-card-${project.id}`}
      className={`glass border ${colors.border} rounded-none overflow-hidden
        transition-all duration-300 hover:border-opacity-60`}
    >
      {/* Header - Always visible */}
      <button
        onClick={onToggle}
        data-testid={`project-toggle-${project.id}`}
        className="w-full p-6 text-left flex items-start justify-between gap-4"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span className={`text-xs uppercase tracking-[0.2em] ${colors.text} font-body font-bold`}>
              {company?.name}
            </span>
          </div>
          <h4 className="font-heading text-xl md:text-2xl font-semibold text-foreground">
            {project.title}
          </h4>
          <p className="font-body text-sm text-muted-foreground mt-2 line-clamp-2">
            {project.description}
          </p>
        </div>
        <motion.div
          animate={{ rotate: isExpanded ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className={`${colors.text} flex-shrink-0`}
        >
          <ChevronDown size={24} />
        </motion.div>
      </button>

      {/* Expanded Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 border-t border-white/10 pt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Context */}
                <div className={`p-4 ${colors.bg} rounded-none`}>
                  <h5 className={`text-xs uppercase tracking-wider ${colors.text} font-body font-bold mb-2`}>
                    Context
                  </h5>
                  <p className="font-body text-sm text-muted-foreground">
                    {project.context}
                  </p>
                </div>

                {/* Risk */}
                <div className="p-4 bg-temple-red/10 rounded-none">
                  <h5 className="text-xs uppercase tracking-wider text-temple-red font-body font-bold mb-2">
                    Risk Identified
                  </h5>
                  <p className="font-body text-sm text-muted-foreground">
                    {project.risk}
                  </p>
                </div>

                {/* Action */}
                <div className="p-4 bg-white/5 rounded-none">
                  <h5 className="text-xs uppercase tracking-wider text-foreground font-body font-bold mb-2">
                    Action Taken
                  </h5>
                  <p className="font-body text-sm text-muted-foreground">
                    {project.action}
                  </p>
                </div>

                {/* Outcome */}
                <div className={`p-4 ${colors.bg} rounded-none`}>
                  <h5 className={`text-xs uppercase tracking-wider ${colors.text} font-body font-bold mb-2`}>
                    Outcome
                  </h5>
                  <p className="font-body text-sm text-muted-foreground">
                    {project.outcome}
                  </p>
                </div>
              </div>

              {/* Tags */}
              {project.tags && project.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-6">
                  {project.tags.map((tag, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1 text-xs font-body uppercase tracking-wider 
                        bg-white/5 border border-white/10 text-muted-foreground"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function ExperienceSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [projects, setProjects] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    if (!API_URL) {
      setProjects(FALLBACK_PROJECTS);
      setLoading(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/projects`);
      if (response.ok) {
        const data = await response.json();
        setProjects(Array.isArray(data) && data.length > 0 ? data : FALLBACK_PROJECTS);
      } else {
        setProjects(FALLBACK_PROJECTS);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects(FALLBACK_PROJECTS);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = selectedCompany 
    ? projects.filter(p => p.company === selectedCompany)
    : projects;

  const allstateProjects = projects.filter(p => p.company === 'allstate');
  const kpmgProjects = projects.filter(p => p.company === 'kpmg');

  return (
    <section 
      id="experience"
      data-testid="experience-section"
      ref={ref}
      className="relative py-32 px-6 bg-card/30"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.3em] text-temple-red font-body">
            Professional Journey
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-semibold text-foreground mt-4">
            What Does My <span className="text-vedic-gold italic">Day Job</span> Look Like?
          </h2>
          <p className="font-body text-muted-foreground mt-6 text-lg max-w-3xl">
            A showcase of high-impact projects where I've navigated complex systems, 
            identified critical risks, and delivered measurable outcomes, all alongside 
            some truly amazing teams.
          </p>
          <p className="font-body text-muted-foreground mt-4 text-lg max-w-3xl">
            Every one of these projects has taught me something 
            that went beyond the work itself, shaping me into a better professional and, 
            honestly, a better person. What you'll find here are brief summaries, just 
            enough to give you a sense of what I do, without getting lost in the weeds.
          </p>
        </motion.div>

        {/* Company Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex flex-wrap gap-4 mb-12"
        >
          <button
            onClick={() => setSelectedCompany(null)}
            data-testid="filter-all"
            className={`px-6 py-3 font-body text-sm uppercase tracking-widest transition-all duration-300
              ${selectedCompany === null 
                ? 'bg-vedic-gold text-black' 
                : 'bg-transparent border border-white/20 text-muted-foreground hover:border-vedic-gold/50'
              }`}
          >
            All Projects ({projects.length})
          </button>
          
          <button
            onClick={() => setSelectedCompany('allstate')}
            data-testid="filter-allstate"
            className={`px-6 py-3 font-body text-sm uppercase tracking-widest transition-all duration-300
              flex items-center gap-3
              ${selectedCompany === 'allstate' 
                ? 'bg-vedic-gold text-black' 
                : 'bg-transparent border border-white/20 text-muted-foreground hover:border-vedic-gold/50'
              }`}
          >
            <span className="font-heading font-bold text-lg">A</span>
            Allstate ({allstateProjects.length})
          </button>
          
          <button
            onClick={() => setSelectedCompany('kpmg')}
            data-testid="filter-kpmg"
            className={`px-6 py-3 font-body text-sm uppercase tracking-widest transition-all duration-300
              flex items-center gap-3
              ${selectedCompany === 'kpmg' 
                ? 'bg-deep-ochre text-black' 
                : 'bg-transparent border border-white/20 text-muted-foreground hover:border-deep-ochre/50'
              }`}
          >
            <span className="font-heading font-bold text-lg text-[#00338D]">K</span>
            KPMG ({kpmgProjects.length})
          </button>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-vedic-gold/50 via-deep-ochre/30 to-temple-red/20 hidden md:block" />

          {/* Projects */}
          {loading ? (
            <div className="text-center py-20">
              <div className="w-8 h-8 border-2 border-vedic-gold border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-muted-foreground mt-4 font-body">Loading projects...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  className={`relative md:w-[calc(50%-2rem)] ${
                    index % 2 === 0 ? 'md:mr-auto' : 'md:ml-auto'
                  }`}
                >
                  {/* Timeline dot */}
                  <div className={`hidden md:block absolute top-6 ${
                    index % 2 === 0 ? '-right-[2.5rem]' : '-left-[2.5rem]'
                  } w-4 h-4 rounded-full border-2 ${
                    project.company === 'allstate' 
                      ? 'border-vedic-gold bg-vedic-gold/20' 
                      : 'border-deep-ochre bg-deep-ochre/20'
                  }`} />
                  
                  <ProjectCard 
                    project={project}
                    isExpanded={expandedProject === project.id}
                    onToggle={() => setExpandedProject(
                      expandedProject === project.id ? null : project.id
                    )}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>

      </div>
    </section>
  );
}
